import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  BubbleChatQuestionIcon,
  GitBranchIcon,
  ShuffleIcon,
  User02Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { FieldTypes } from 'constants/field-types-enum';
import { FormTypes } from 'constants/forms';
import { useAccessControl } from 'hooks/access-control';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { debounce } from 'utils/requestHelpers';

import Alert from 'components/Alert/Alert';
import { FormSettings } from 'components/Form/FormEdit/FormSettings/FormSettings';

import { selectPermissions } from 'api/user-permissions-slice';

import LogicTab from './LogicTab';
import QuestionsTab from './QuestionsTab';
import { DEFAULT_EMPTY_FIELD, useQuestionnaireBuilder } from './useQuestionnaireBuilder';

const REFEREE_QUESTIONS_TAB = {
  title: 'Questions',
  icon: BubbleChatQuestionIcon,
  description:
    'Referee questions supplement verification questions. They are fully customisable, with logic jumps to skip questions based on set rules.',
};

const CANDIDATE_QUESTIONS_TAB = {
  title: 'Questions',
  icon: BubbleChatQuestionIcon,
  description:
    'Candidate questions are questions that are asked to the candidate. They are fully customisable, with logic jumps to skip questions based on set rules.',
};

const CANDIDATE_QUESTIONNAIRE_TAB = {
  title: 'Questions',
  icon: BubbleChatQuestionIcon,
  description:
    'Candidate questionnaires are questions that are asked to the candidate for questionnaire check. They are fully customisable, with logic jumps to skip questions based on set rules.',
};

const QUESTIONS_TAB_BY_FORM_TYPE = {
  [FormTypes.REFEREE]: REFEREE_QUESTIONS_TAB,
  [FormTypes.CANDIDATE]: CANDIDATE_QUESTIONS_TAB,
  [FormTypes.QUESTIONNAIRE]: CANDIDATE_QUESTIONNAIRE_TAB,
};

const QuestionnaireBuilder = forwardRef(
  (
    {
      form,
      fieldsFromAIGenerator = [],
      textParams = {},
      onSave = () => {},
      sidebarCollapsed = false,
      isUsingModernForm = false,
      showFormSettings = false,
      onCloseFormSettings = () => {},
      teams = [],
    },
    ref,
  ) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const activeItem = searchParams.get('tab') || 'questionnaire_questions';
    const formRef = useRef(null);

    const isCandidateForm =
      form?.type === FormTypes.CANDIDATE || form?.type === FormTypes.QUESTIONNAIRE;

    const tabDetails = useMemo(() => {
      const questionsTab = QUESTIONS_TAB_BY_FORM_TYPE[form?.type] ?? REFEREE_QUESTIONS_TAB;
      return {
        questionnaire_questions: {
          ...questionsTab,
          count: form?.fields?.length || 0,
        },
        logic_jumps: {
          title: 'Logic jumps',
          icon: GitBranchIcon,
          description: 'Logic jumps are used to skip questions based on set rules.',
          count: form?.logic_jumps?.length || 0,
        },
      };
    }, [form?.type, form?.fields?.length, form?.logic_jumps?.length, isCandidateForm]);

    const initialFieldsRef = useRef(
      form?.fields?.length > 0 ? form.fields : [{ ...DEFAULT_EMPTY_FIELD, order: 1 }],
    );

    const {
      control,
      getValues,
      handleSubmit,
      register,
      reset,
      setValue,
      unregister,
      watch,
      formState: { errors },
    } = useForm({
      defaultValues: {
        rules: [],
        ...form,
        message: form?.message ?? '',
        fields: initialFieldsRef.current,
      },
    });

    const permissions = useSelector(selectPermissions);
    const hasAccess = useAccessControl(permissions);

    const {
      fields,
      append,
      move,
      remove: removeField,
    } = useFieldArray({ control, name: 'fields', keyName: 'key' });

    const formValues = useWatch({ control });

    const {
      fields: logicJumps,
      append: appendLogic,
      remove: removeLogic,
    } = useFieldArray({ control, name: 'logic_jumps', keyName: 'key' });

    const { fields: formRules } = useFieldArray({ control, name: 'rules', keyName: 'key' });

    const [copyFieldDetails, setCopyFieldDetails] = useState(null);
    const [fieldReorderAlert, setFieldReorderAlert] = useState(false);
    const [dragInfo, setDragInfo] = useState(null);
    const [logicJumpAlertMessage, setLogicJumpAlertMessage] = useState('');
    const [deleteFieldAlert, setDeleteFieldAlert] = useState(false);
    const [deleteFieldMessage, setDeleteFieldMessage] = useState('');
    const [deleteFieldIndex, setDeleteFieldIndex] = useState(null);
    const [deleteFieldLogicJumps, setDeleteFieldLogicJumps] = useState([]);
    const [blockBeforeFields, setBlockBeforeFields] = useState(0);

    const addIntroField = useCallback(() => {
      append({
        order: 1,
        text: '',
        check_part: 'CUSTOM',
        type: FieldTypes.Statement,
        required: true,
        options: { required: true, intro: true, options: [{ value: '' }] },
      });
      move(fields.length, 0);
      setBlockBeforeFields(b => b + 1);
    }, [append, move, fields.length]);

    const handleHasIntroField = useCallback(
      hasIntro => {
        if (hasIntro) {
          addIntroField();
        } else {
          removeField(0);
          setBlockBeforeFields(b => b - 1);
        }
      },
      [addIntroField, removeField],
    );

    const hasIntroField = useMemo(() => fields?.some(f => f?.options?.intro) ?? false, [fields]);

    const {
      addField,
      onDragEnd,
      reorderFields,
      handleDeleteField,
      duplicateField,
      copyField,
      pasteFieldBelow,
      pasteFieldAbove,
      getLogicJumpsCount,
      getRulesCount,
    } = useQuestionnaireBuilder({
      fields,
      logicJumps,
      formRules,
      append,
      move,
      removeLogic,
      setValue,
      blockBeforeFields,
      setCopyFieldDetails,
      copyFieldDetails,
      setLogicJumpAlertMessage,
      setDragInfo,
      setFieldReorderAlert,
      setDeleteFieldLogicJumps,
      setDeleteFieldAlert,
      setDeleteFieldMessage,
      setDeleteFieldIndex,
    });

    const [question, setQuestion] = useState(() => fields[0] ?? null);
    const [questionIndex, setQuestionIndex] = useState(0);
    const [draggingId, setDraggingId] = useState(null);
    const questionRefs = useRef({});
    const pendingNewFieldIndex = useRef(null);
    const AUTO_SAVE_DEBOUNCE_MS = 1500; // allow programmatic sync to settle before first autosave
    const autoSaveReadyAfter = useRef(Date.now() + AUTO_SAVE_DEBOUNCE_MS);
    const lastAutoSaveSnapshotRef = useRef(null);
    const onSaveRef = useRef(onSave);
    const getValuesRef = useRef(getValues);
    onSaveRef.current = onSave;
    getValuesRef.current = getValues;

    const debouncedAutoSave = useRef(
      debounce(
        () => {
          onSaveRef.current?.(getValuesRef.current(), { isAutoSave: true });
        },
        300000,
        { maxWait: 300000 },
      ),
    ).current;

    useEffect(() => {
      if (!form) return;
      // Reset form when form prop changes (e.g. after save + refetch) so nodes and UI show latest data
      const formFields = form.fields ?? [];
      const fieldsWithKeys =
        formFields.length > 0
          ? formFields.map((f, i) => ({
              ...f,
              key: f.key ?? f.id ?? `field-${i}-${f.order ?? i}`,
            }))
          : [{ ...DEFAULT_EMPTY_FIELD, order: 1, key: 'field-0-1' }];
      reset({
        rules: form.rules ?? [],
        ...form,
        message: form.message ?? '',
        fields: fieldsWithKeys,
      });
      // Defer auto-save until after programmatic sync (e.g. field order) has settled
      autoSaveReadyAfter.current = Date.now() + AUTO_SAVE_DEBOUNCE_MS;
      lastAutoSaveSnapshotRef.current = null;
    }, [form, reset]);

    useEffect(() => {
      if (fields.length === 0) {
        handleAddField();
      }
    }, []);

    useEffect(() => {
      if (fieldsFromAIGenerator.length > 0) {
        if (fields.length === 1 && fields[0].text === DEFAULT_EMPTY_FIELD.text) {
          removeField(0);
        }
        fieldsFromAIGenerator.forEach(field => {
          append(field);
        });
      }
    }, [fieldsFromAIGenerator, append, form?.id]);

    // When a new field was just added, make it active so scroll runs
    useEffect(() => {
      if (pendingNewFieldIndex.current !== null && fields.length > pendingNewFieldIndex.current) {
        const index = pendingNewFieldIndex.current;
        setQuestion(fields[index]);
        setQuestionIndex(index);
        pendingNewFieldIndex.current = null;
      }
    }, [fields.length, fields]);

    // Scroll to selected question
    useEffect(() => {
      if (questionIndex !== null && questionRefs.current[questionIndex]) {
        questionRefs.current[questionIndex].scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }, [questionIndex]);

    useEffect(() => {
      if (!fields?.length) return;
      const updatedFields = fields.map((field, idx) => ({ ...field, order: idx + 1 }));
      setValue('fields', updatedFields);
    }, [fields.length, setValue]); // intentionally sync order from current fields when length changes

    // Auto-save: only when form values actually changed (not on tab switch or re-renders)
    useEffect(() => {
      if (!form) return;
      const snapshot = () => JSON.stringify(getValuesRef.current());
      if (Date.now() < autoSaveReadyAfter.current) {
        lastAutoSaveSnapshotRef.current = snapshot();
        return;
      }
      const current = snapshot();
      if (lastAutoSaveSnapshotRef.current === current) return;
      lastAutoSaveSnapshotRef.current = current;
      debouncedAutoSave();
    }, [formValues, form, debouncedAutoSave]);

    useEffect(() => {
      return () => debouncedAutoSave.cancel();
    }, [debouncedAutoSave]);

    const setQuestionPreview = useCallback((field, index) => {
      setQuestion(field);
      setQuestionIndex(index);
    }, []);

    const handleAddField = useCallback(() => {
      addField();
      pendingNewFieldIndex.current = fields.length;
    }, [addField, fields.length]);

    const handleFieldChange = useCallback(
      (field, index) => {
        // Update the question preview if this is the selected question
        if (question && question.key === field.key) {
          setQuestion(field);
          setQuestionIndex(index);
        }
      },
      [question],
    );

    const getFieldErrorMessage = useCallback(fieldError => {
      if (!fieldError) return null;
      if (typeof fieldError === 'string') return fieldError;
      if (fieldError.message !== undefined && fieldError.message !== null) {
        return (
          fieldError.message ||
          (fieldError.type === 'required' ? 'This field is required' : 'Invalid value')
        );
      }
      const firstKey = Object.keys(fieldError)[0];
      if (firstKey) return getFieldErrorMessage(fieldError[firstKey]);
      return null;
    }, []);

    const onError = () => {
      // Validation errors are shown per-question via ValidationAlert below
    };

    const handleRemoveLogic = useCallback(
      index => {
        removeLogic(index);
      },
      [removeLogic],
    );

    const handleFieldReorder = useCallback(() => {
      if (dragInfo) {
        reorderFields(dragInfo.source, dragInfo.destination, dragInfo.logicJumpsAttached);
        setDragInfo(null);
      }
      setFieldReorderAlert(false);
      setLogicJumpAlertMessage('');
    }, [dragInfo, reorderFields, setFieldReorderAlert]);

    const handleDeleteFieldConfirm = useCallback(() => {
      if (deleteFieldIndex === null) return;
      removeField(deleteFieldIndex);
      if (deleteFieldIndex === 0) {
        setBlockBeforeFields(prev => Math.max(0, prev - 1));
      }
      const indicesToRemove = [
        ...new Set(
          deleteFieldLogicJumps
            ?.map(jump =>
              logicJumps.findIndex(
                lj => lj.from === jump.from && lj.to === jump.to && lj.field === jump.field,
              ),
            )
            .filter(index => index !== -1) ?? [],
        ),
      ].sort((a, b) => b - a);
      indicesToRemove.forEach(index => removeLogic(index));
      setDeleteFieldAlert(false);
      setDeleteFieldMessage('');
      setDeleteFieldIndex(null);
      setDeleteFieldLogicJumps([]);
      setQuestion(null);
      setQuestionIndex(null);
    }, [deleteFieldIndex, deleteFieldLogicJumps, logicJumps, removeField, removeLogic]);

    const handleSave = data => {
      // Cancel any pending auto-save and sync the snapshot so auto-save
      // doesn't fire again immediately after the form prop updates from the server
      debouncedAutoSave.cancel();
      lastAutoSaveSnapshotRef.current = JSON.stringify(data);
      onSave(data);
    };

    useImperativeHandle(
      ref,
      () => ({
        submit: () => {
          if (formRef.current) {
            formRef.current.requestSubmit();
          } else {
            handleSave(getValues());
          }
        },
        setFormName: name => setValue('name', name),
        getFormValues: () => getValues(),
      }),
      [handleSave, getValues, setValue],
    );

    if (!form) {
      return <div>No form found</div>;
    }

    return (
      <>
        <Alert
          show={fieldReorderAlert}
          title="You have logic setup. Are you sure you want to continue?"
          message={`The following fields will be affected by this change: <br/><br/>${logicJumpAlertMessage}`}
          ok="Confirm"
          type="error"
          onOk={handleFieldReorder}
          icon={ShuffleIcon}
          cancel="Cancel"
          onCancel={() => {
            setFieldReorderAlert(false);
            setDragInfo(null);
            setLogicJumpAlertMessage('');
          }}
        />
        <Alert
          show={deleteFieldAlert}
          title={`${deleteFieldLogicJumps?.length > 0 ? 'You have logic setup. Are you sure you want to continue?' : 'Are you sure you want to delete this field?'}`}
          message={`${deleteFieldLogicJumps?.length > 0 ? 'The following logic jumps will be removed: <br/><br/>' : ''} ${deleteFieldMessage}`}
          ok="Confirm"
          type="error"
          onOk={handleDeleteFieldConfirm}
          icon={ShuffleIcon}
          cancel="Cancel"
          onCancel={() => {
            setDeleteFieldAlert(false);
            setDeleteFieldMessage('');
            setDeleteFieldIndex(null);
            setDeleteFieldLogicJumps([]);
          }}
        />
        <FormSettings
          visible={showFormSettings}
          onClose={onCloseFormSettings}
          form={form}
          teams={teams}
          register={register}
          control={control}
          watch={watch}
          setValue={setValue}
          hasAccess={hasAccess}
          hasIntroField={hasIntroField}
          onHasIntroFieldChange={handleHasIntroField}
          onSave={() => formRef.current?.requestSubmit()}
        />
        <main
          className={cn(
            'pt-header min-h-screen bg-background transition-[margin-left] duration-200',
            sidebarCollapsed ? 'ml-20' : 'ml-sidebar max-[1140px]:ml-20',
          )}>
          <div
            className={cn(
              'sticky top-header right-0 h-subheader bg-white border-b border-neutral-4 z-30 flex items-start justify-between py-8 pr-8 pl-10 shadow-sm transition-[left] duration-200',
              sidebarCollapsed ? 'left-20' : 'left-sidebar max-[1140px]:left-20',
            )}>
            <div className="flex flex-col gap-2">
              <span className="text-xl font-semibold text-neutral-6 flex items-center">
                {isCandidateForm ? 'Candidate' : 'Referee'}
              </span>
              <div className="flex items-center gap-3">
                <HugeiconsIcon
                  icon={tabDetails[activeItem]?.icon ?? tabDetails.questionnaire_questions?.icon}
                  size="24"
                  className="text-neutral-7"
                />
                <span className="text-3xl font-medium text-neutral-7 flex items-center gap-2">
                  {tabDetails[activeItem]?.title ?? tabDetails.questionnaire_questions?.title}
                </span>
                <span className="text-xl text-neutral-6 bg-neutral-3 w-10 h-10 rounded-full border border-neutral-4 flex items-center justify-center">
                  {tabDetails[activeItem]?.count ?? tabDetails.questionnaire_questions?.count ?? 0}
                </span>
              </div>
              <p className="text-xl text-neutral-6 font-regular">
                {tabDetails[activeItem]?.description ??
                  tabDetails.questionnaire_questions?.description ??
                  ''}
              </p>
            </div>
          </div>
          {activeItem === 'logic_jumps' && (
            <LogicTab
              control={control}
              register={register}
              setValue={setValue}
              blockBefore={blockBeforeFields}
              appendLogic={appendLogic}
              removeLogic={handleRemoveLogic}
              logicJumpsFields={logicJumps}
              textParams={textParams}
              fields={fields}
              locale="en_GB"
              sidebarCollapsed={sidebarCollapsed}
            />
          )}
          {activeItem === 'questionnaire_questions' && (
            <QuestionsTab
              formRef={formRef}
              handleSubmit={handleSubmit}
              handleSave={handleSave}
              onError={onError}
              fields={fields}
              errors={errors}
              getFieldErrorMessage={getFieldErrorMessage}
              question={question}
              questionIndex={questionIndex}
              questionRefs={questionRefs}
              textParams={textParams}
              setQuestionPreview={setQuestionPreview}
              handleAddField={handleAddField}
              handleFieldChange={handleFieldChange}
              handleDeleteField={handleDeleteField}
              duplicateField={duplicateField}
              copyField={copyField}
              pasteFieldBelow={pasteFieldBelow}
              pasteFieldAbove={pasteFieldAbove}
              copyFieldDetails={copyFieldDetails}
              getLogicJumpsCount={getLogicJumpsCount}
              getRulesCount={getRulesCount}
              register={register}
              watch={watch}
              control={control}
              setValue={setValue}
              unregister={unregister}
              onDragEnd={onDragEnd}
              setDraggingId={setDraggingId}
              draggingId={draggingId}
              sidebarCollapsed={sidebarCollapsed}
              isUsingModernForm={isUsingModernForm}
            />
          )}
        </main>
      </>
    );
  },
);

QuestionnaireBuilder.displayName = 'QuestionnaireBuilder';

export default QuestionnaireBuilder;
